import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, AutoAwesome as AutoIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect, { type SelectOption } from '../SearchableSelect';
import loteService, { LoteAsignacion, ProductoLote } from '../../services/loteService';
import LoteAsistenteDialog from './LoteAsistenteDialog';
import LoteAdjuntosPanel from './LoteAdjuntosPanel';

export interface LoteAsignacionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (lotes: LoteAsignacion[]) => void;
  codigoArticulo: string;
  cantidad: number;
  nroUbicacion: number;
  mode: 'ingreso' | 'egreso' | 'transferencia';
  esSerie?: boolean;
  requiereVencimiento?: boolean;
  initialLotes?: LoteAsignacion[];
  /** Solo consulta: muestra lotes sin editar. */
  readOnly?: boolean;
  /** Si se informa, solo se pueden elegir estos lotes (p. ej. facturados en NC). */
  lotesPermitidos?: LoteAsignacion[] | null;
}

const emptyRow = (cantidadFila = 1): LoteAsignacion => ({
  idLote: null,
  nroLote: '',
  fechaVencimiento: null,
  fechaFabricacion: null,
  cantidad: cantidadFila > 0 ? cantidadFila : 1,
});

const LoteAsignacionDialog: React.FC<LoteAsignacionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  codigoArticulo,
  cantidad,
  nroUbicacion,
  mode,
  esSerie = false,
  requiereVencimiento = false,
  initialLotes,
  readOnly = false,
  lotesPermitidos,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<LoteAsignacion[]>([]);
  const [existentes, setExistentes] = useState<ProductoLote[]>([]);
  const [asistenteOpen, setAsistenteOpen] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  /** Lotes del artículo con saldo en otra ubicación (o sin filtrar), para explicar "Sin lotes". */
  const [avisoSinSaldoUbicacion, setAvisoSinSaldoUbicacion] = useState<string | null>(null);

  const suma = useMemo(() => rows.reduce((a, r) => a + (Number(r.cantidad) || 0), 0), [rows]);
  const restringido = (lotesPermitidos?.length ?? 0) > 0;
  const isEgreso = mode === 'egreso' || mode === 'transferencia';
  const usarSelector = isEgreso || restringido;
  const ubicacionEgresoValida = Number(nroUbicacion) > 0;

  /** Lotes seleccionados con id (p. ej. egreso en remito de venta) para descargar certificados. */
  const lotesConAdjuntos = useMemo(() => {
    const seen = new Set<number>();
    const list: Array<{ idLote: number; nroLote: string }> = [];
    for (const r of rows) {
      const id = r.idLote != null ? Number(r.idLote) : 0;
      if (id <= 0 || seen.has(id)) continue;
      seen.add(id);
      list.push({ idLote: id, nroLote: String(r.nroLote ?? '').trim() || `Lote ${id}` });
    }
    return list;
  }, [rows]);

  const saldoEnUbicacion = (lote: ProductoLote): number => {
    if (!ubicacionEgresoValida || !Array.isArray(lote.depositos) || lote.depositos.length === 0) {
      return Number(lote.cantidadTotal) || 0;
    }
    const enUb = lote.depositos.find((d) => Number(d.nroUbicacion) === Number(nroUbicacion));
    return enUb != null ? Number(enUb.cantidad) || 0 : Number(lote.cantidadTotal) || 0;
  };

  const loteOptions = useMemo<SelectOption<number>[]>(
    () =>
      existentes.map((e) => ({
        value: e.idLote,
        label: [
          e.nroLote,
          e.fechaVencimiento ? `(vto ${String(e.fechaVencimiento).slice(0, 10)})` : null,
          `— ${saldoEnUbicacion(e)}`,
        ]
          .filter(Boolean)
          .join(' '),
      })),
    // saldoEnUbicacion depende de nroUbicacion/existentes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [existentes, nroUbicacion],
  );

  const cantidadInicialFila = esSerie
    ? 1
    : Number(cantidad) > 0
      ? Number(cantidad)
      : 1;

  useEffect(() => {
    if (!open) return;
    setRows(
      initialLotes && initialLotes.length > 0
        ? initialLotes.map((l) => ({ ...l }))
        : [emptyRow(cantidadInicialFila)],
    );
    setAvisoSinSaldoUbicacion(null);

    if (readOnly) {
      setExistentes([]);
      setLoadingSuggest(false);
      return;
    }

    if (restringido) {
      const permitidos = (lotesPermitidos ?? [])
        .filter((l) => l.idLote != null && Number(l.idLote) > 0)
        .map(
          (l): ProductoLote => ({
            idLote: Number(l.idLote),
            codigoArticulo,
            nroLote: String(l.nroLote ?? '').trim(),
            esSerie: false,
            fechaFabricacion: l.fechaFabricacion ?? null,
            fechaVencimiento: l.fechaVencimiento ?? null,
            estado: 'A',
            fechaAlta: '',
            cantidadTotal: Number(l.cantidad) || 0,
            depositos: [],
            observaciones: l.observaciones ?? null,
          }),
        );
      setExistentes(permitidos);
      setLoadingSuggest(false);
      return;
    }

    if (isEgreso && !ubicacionEgresoValida) {
      setExistentes([]);
      setAvisoSinSaldoUbicacion(
        'Indicá la ubicación de egreso del remito/comprobante. Sin ubicación no se pueden listar lotes con stock.',
      );
      setLoadingSuggest(false);
      return;
    }

    let cancelled = false;
    loteService
      .search({
        codigoArticulo,
        nroUbicacion: isEgreso ? nroUbicacion : undefined,
        soloConSaldo: isEgreso,
        pageSize: 100,
      })
      .then(async (r) => {
        if (cancelled) return;
        const items = r.items ?? [];
        setExistentes(items);
        if (!isEgreso || items.length > 0) return;

        // Vacío en esta ubicación: ¿hay lotes del artículo en otra parte?
        try {
          const global = await loteService.search({
            codigoArticulo,
            soloConSaldo: true,
            pageSize: 20,
          });
          if (cancelled) return;
          const otros = (global.items ?? []).filter((l) => (Number(l.cantidadTotal) || 0) > 0);
          if (otros.length === 0) {
            setAvisoSinSaldoUbicacion(
              'No hay lotes con saldo para este artículo. El listado de Lotes puede mostrar lotes en cero; el egreso solo ofrece lotes con stock.',
            );
            return;
          }
          const ejemplos = otros
            .slice(0, 3)
            .map((l) => {
              const deps = (l.depositos ?? [])
                .filter((d) => (Number(d.cantidad) || 0) > 0)
                .map((d) => d.nombreUbicacion || `ubic. ${d.nroUbicacion}`)
                .join(', ');
              return deps ? `${l.nroLote} (${deps})` : `${l.nroLote} (saldo ${l.cantidadTotal})`;
            })
            .join('; ');
          setAvisoSinSaldoUbicacion(
            `No hay lotes con saldo en la ubicación ${nroUbicacion}. En otras ubicaciones: ${ejemplos}. Cambiá la ubicación de egreso o transferí stock.`,
          );
        } catch {
          if (!cancelled) {
            setAvisoSinSaldoUbicacion(
              `No hay lotes con saldo en la ubicación ${nroUbicacion}. Revisá en Lotes en qué depósito está el stock.`,
            );
          }
        }
      })
      .catch(() => {
        if (!cancelled) setExistentes([]);
      });

    if (isEgreso && ubicacionEgresoValida && (!initialLotes || initialLotes.length === 0) && cantidad > 0) {
      setLoadingSuggest(true);
      loteService
        .sugerir(codigoArticulo, nroUbicacion, cantidad)
        .then((sug) => {
          if (cancelled || sug.length === 0) return;
          setRows(
            sug.map((s) => ({
              idLote: s.idLote,
              nroLote: s.nroLote,
              fechaVencimiento: s.fechaVencimiento,
              cantidad: s.cantidadSugerida,
            })),
          );
        })
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled) setLoadingSuggest(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [
    open,
    codigoArticulo,
    nroUbicacion,
    cantidad,
    cantidadInicialFila,
    mode,
    isEgreso,
    initialLotes,
    readOnly,
    restringido,
    lotesPermitidos,
    ubicacionEgresoValida,
  ]);

  const filaSinLoteAsignado = (r: LoteAsignacion) =>
    !String(r.nroLote ?? '').trim() && (r.idLote == null || Number(r.idLote) <= 0);

  /** Remanente respecto de la cantidad del renglón, excluyendo índices indicados. */
  const cantidadRemanente = (filas: LoteAsignacion[], excluirIdx: number[]) => {
    const excl = new Set(excluirIdx);
    const asignado = filas.reduce(
      (acc, r, i) => (excl.has(i) ? acc : acc + (Number(r.cantidad) || 0)),
      0,
    );
    return Math.max(0, Number(cantidad) - asignado);
  };

  const updateRow = (index: number, patch: Partial<LoteAsignacion>) => {
    if (readOnly) return;
    setRows((prev) => {
      let next = prev.map((r, i) => (i === index ? { ...r, ...patch } : r));
      // Si cambia la cantidad, la última fila vacía (sin nro de lote) toma el remanente.
      if (!esSerie && patch.cantidad !== undefined && next.length > 1) {
        let lastEmpty = -1;
        for (let i = next.length - 1; i >= 0; i -= 1) {
          if (i !== index && filaSinLoteAsignado(next[i])) {
            lastEmpty = i;
            break;
          }
        }
        if (lastEmpty >= 0) {
          const restante = cantidadRemanente(next, [lastEmpty]);
          next = next.map((r, i) =>
            i === lastEmpty ? { ...r, cantidad: restante > 0 ? restante : 1 } : r,
          );
        }
      }
      return next;
    });
  };

  const handleSelectExistente = (index: number, idLote: number) => {
    const found = existentes.find((e) => e.idLote === idLote);
    if (!found) return;
    updateRow(index, {
      idLote: found.idLote,
      nroLote: found.nroLote,
      fechaVencimiento: found.fechaVencimiento,
      fechaFabricacion: found.fechaFabricacion,
    });
  };

  const handleConfirm = () => {
    if (readOnly) {
      onClose();
      return;
    }
    if (Math.abs(suma - cantidad) > 0.0005) {
      enqueueSnackbar(`La suma de lotes (${suma}) debe igualar la cantidad (${cantidad})`, {
        variant: 'warning',
      });
      return;
    }
    for (const r of rows) {
      if (!r.idLote && !r.nroLote?.trim()) {
        enqueueSnackbar('Cada fila debe tener número de lote/serie', { variant: 'warning' });
        return;
      }
      if ((isEgreso || restringido) && !r.idLote) {
        enqueueSnackbar(
          restringido
            ? 'Debe seleccionar un lote de los facturados'
            : 'En egresos debe seleccionar lotes existentes',
          { variant: 'warning' },
        );
        return;
      }
      if (requiereVencimiento && !r.idLote && !r.fechaVencimiento) {
        enqueueSnackbar('La fecha de vencimiento es obligatoria', { variant: 'warning' });
        return;
      }
      if (esSerie && Number(r.cantidad) !== 1) {
        enqueueSnackbar('Las series solo admiten cantidad 1', { variant: 'warning' });
        return;
      }
    }
    onConfirm(rows);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {readOnly ? 'Lotes' : 'Asignar'} {esSerie ? 'series' : 'lotes'} — {codigoArticulo}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Cantidad requerida: {cantidad} · Asignado: {suma}
              {loadingSuggest ? ' · sugiriendo FEFO…' : ''}
            </Typography>
            {!readOnly && (
              <Stack direction="row" spacing={1}>
                {!usarSelector && (
                  <Button size="small" startIcon={<AutoIcon />} onClick={() => setAsistenteOpen(true)}>
                    Asistente
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setRows((prev) => {
                      const restante = cantidadRemanente(prev, []);
                      const qty = esSerie ? 1 : restante > 0 ? restante : 1;
                      return [...prev, emptyRow(qty)];
                    })
                  }
                >
                  Agregar
                </Button>
              </Stack>
            )}
          </Stack>
          {restringido && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Solo lotes incluidos en la factura origen
            </Typography>
          )}
          {isEgreso && ubicacionEgresoValida && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Solo lotes con saldo en la ubicación de egreso ({nroUbicacion}). El listado de Lotes muestra el
              saldo total de todos los depósitos.
            </Typography>
          )}
          {avisoSinSaldoUbicacion && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              {avisoSinSaldoUbicacion}
            </Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lote / Serie</TableCell>
                <TableCell>Vencimiento</TableCell>
                <TableCell width={120}>Cantidad</TableCell>
                {!readOnly && <TableCell width={56} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2">
                        {row.nroLote || '—'}
                        {row.fechaVencimiento
                          ? ` (vto ${String(row.fechaVencimiento).slice(0, 10)})`
                          : ''}
                      </Typography>
                    ) : usarSelector ? (
                      <SearchableSelect<number>
                        size="small"
                        value={row.idLote ?? null}
                        onChange={(idLote) => {
                          if (idLote == null) {
                            updateRow(index, {
                              idLote: null,
                              nroLote: '',
                              fechaVencimiento: null,
                              fechaFabricacion: null,
                            });
                            return;
                          }
                          handleSelectExistente(index, idLote);
                        }}
                        options={loteOptions}
                        placeholder={restringido ? 'Lote facturado…' : 'Buscar lote / serie…'}
                        allowEmpty
                        emptyOptionLabel="Seleccionar…"
                        noOptionsText={
                          restringido
                            ? 'Sin lotes en la factura'
                            : avisoSinSaldoUbicacion
                              ? 'Sin saldo en esta ubicación'
                              : 'Sin lotes con saldo'
                        }
                      />
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        value={row.nroLote ?? ''}
                        onChange={(e) =>
                          updateRow(index, { nroLote: e.target.value.toUpperCase(), idLote: null })
                        }
                        placeholder="Nro lote"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2">
                        {row.fechaVencimiento ? String(row.fechaVencimiento).slice(0, 10) : '—'}
                      </Typography>
                    ) : (
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={row.fechaVencimiento ? String(row.fechaVencimiento).slice(0, 10) : ''}
                        onChange={(e) =>
                          updateRow(index, { fechaVencimiento: e.target.value || null })
                        }
                        disabled={!!row.idLote && usarSelector}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2">{row.cantidad}</Typography>
                    ) : (
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        value={row.cantidad}
                        onChange={(e) => updateRow(index, { cantidad: Number(e.target.value) || 0 })}
                        inputProps={{ min: esSerie ? 1 : 0.001, step: esSerie ? 1 : 0.001 }}
                        disabled={esSerie}
                      />
                    )}
                  </TableCell>
                  {!readOnly && (
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                        disabled={rows.length <= 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!readOnly && !usarSelector && existentes.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Lotes existentes del artículo (opcionales para reutilizar):{' '}
                {existentes
                  .slice(0, 8)
                  .map((e) => e.nroLote)
                  .join(', ')}
              </Typography>
            </Box>
          )}

          {lotesConAdjuntos.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                Certificados / documentos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Solo descarga. Seleccioná el lote arriba para ver sus archivos.
              </Typography>
              <Stack spacing={2} divider={<Divider flexItem />}>
                {lotesConAdjuntos.map((l) => (
                  <LoteAdjuntosPanel
                    key={l.idLote}
                    idLote={l.idLote}
                    downloadOnly
                    title={`Documentos — ${l.nroLote}`}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {readOnly ? (
            <Button variant="contained" onClick={onClose}>
              Cerrar
            </Button>
          ) : (
            <>
              <Button onClick={onClose}>Cancelar</Button>
              <Button variant="contained" onClick={handleConfirm}>
                Confirmar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <LoteAsistenteDialog
        open={asistenteOpen}
        onClose={() => setAsistenteOpen(false)}
        codigoArticulo={codigoArticulo}
        esSerie={esSerie}
        cantidadInicial={Math.max(rows.length, 1)}
        onGenerated={(nums) => {
          const generados = (nums ?? []).map((n) => String(n ?? '').trim()).filter(Boolean);
          if (generados.length === 0) return;

          if (esSerie) {
            setRows(generados.map((n) => ({ ...emptyRow(1), nroLote: n, cantidad: 1 })));
            return;
          }

          setRows((prev) => {
            if (prev.length === 0) {
              return [{ ...emptyRow(cantidad), nroLote: generados[0], idLote: null }];
            }
            const next = prev.map((r, i) =>
              generados[i]
                ? { ...r, nroLote: generados[i], idLote: null }
                : r,
            );
            // Si el asistente generó más números que filas, agrega filas nuevas.
            for (let i = prev.length; i < generados.length; i += 1) {
              next.push({ ...emptyRow(1), nroLote: generados[i], idLote: null });
            }
            return next;
          });
        }}
      />
    </>
  );
};

export default LoteAsignacionDialog;
